import React from 'react';

/**
 * Preliminary Intelligence Dashboard mock — built to demo to a Chinese
 * financial-litigation law firm. All data on this page is fabricated
 * for demonstration. The real product would pull from:
 *   - 企查查 / 天眼查 API (paid)
 *   - 国家企业信用信息公示系统 (free, scraping)
 *   - 中国裁判文书网
 *   - 招标 / 知产 / ICP 备案 公开来源
 *   - LLM-summarised social sentiment
 * See INTELLIGENCE_DESIGN.md for the full architecture.
 */

const MOCK = {
  company: '上海智盈金融科技有限公司',
  uscc: '91310115MA1XXXXXXX',
  industry: 'P2P 网贷 / 金融科技',
  registered_capital: '人民币 5,000 万元（实缴 800 万）',
  registered_address: '上海市松江区民强路 ××× 号 3 幢',
  actual_address: '北京市朝阳区建国路（实地核查空置）',
  established: '2018-03-12',
  status: '存续（异常经营）',
  risk: {
    judicial: { score: 8.6, level: 'high', label: '司法风险 高' },
    regulatory: { score: 6.2, level: 'medium', label: '监管风险 中' },
    sentiment: { score: 8.9, level: 'high', label: '舆情风险 高' },
    composite: 8.2,
  },
};

const EQUITY = {
  ubo: { name: '王某某', stake: 52, role: '实际控制人' },
  shareholders: [
    { name: '王某某', stake: 52, type: '自然人' },
    { name: '上海宏远投资有限公司', stake: 28, type: '法人（实控人控股 70%）' },
    { name: '深圳前海智信资本管理有限公司', stake: 15, type: '法人（疑似代持）' },
    { name: '其他个人股东 ×3', stake: 5, type: '自然人' },
  ],
  related_companies: 14,
  offshore: { bvi: 1, cayman: 1 },
  flags: [
    '近 6 个月内 3 家关联公司注销',
    '5 家关联公司注册地集中在霍尔果斯 / 西藏（避税地信号）',
    '2024-Q4 股权两次倒手，最终回到原股东',
    '深圳前海智信资本管理 — 注册资本仅 100 万，疑似代持壳公司',
  ],
};

const LITIGATION = {
  as_defendant: 23,
  as_plaintiff: 9,
  loss_rate: 0.61,
  unfulfilled_judgments: 4,
  recent_cases: [
    { date: '2025-08', court: '上海二中院', topic: '金融借款合同纠纷', amount: '4,800 万', status: '判决履行中' },
    { date: '2025-04', court: '上海浦东法院', topic: '合伙协议纠纷', amount: '1,200 万', status: '终本' },
    { date: '2024-11', court: '北京朝阳法院', topic: '服务合同纠纷', amount: '380 万', status: '已履行' },
  ],
  regulatory: [
    { date: '2025-09', issuer: '上海市地方金融监督管理局', type: '行政处罚', amount: '200 万', summary: '违规开展类信贷业务' },
    { date: '2025-03', issuer: '中国人民银行上海分行', type: '处罚函', amount: '—', summary: '未按规定报送大额交易' },
    { date: '2024-07', issuer: '中国互联网金融协会', type: '自律警示', amount: '—', summary: '信息披露不及时' },
  ],
  on_dishonest_list: true,
  consumption_restriction: { issued: '2025-Q3', target: '王某某' },
};

const ASSETS = {
  bidding: [
    { date: '2025-06', project: '某省人力资源信息平台运维', amount: '1.2 亿', status: '中标' },
    { date: '2025-01', project: '某市政务大数据采购', amount: '4,200 万', status: '中标' },
    { date: '2024-09', project: '某高校金融实训系统', amount: '1,800 万', status: '中标' },
  ],
  ip: { copyright: 12, trademark: 8, patent: 3 },
  domains: ['zhiyingfin.com', 'zhiying-tech.cn', 'zybank.app'],
  hints: [
    '中标款多走 上海银行松江支行（推测：申请调查令优先调取该行流水）',
    '5 项软著未质押，可直接代位执行（中国版权保护中心可直接登记转让）',
    'zybank.app 月活 ~12 万，关联充值流水属未公开收入',
  ],
};

const SENTIMENT = {
  negative_count_30d: 38,
  trend: 'rising',
  buckets: [
    { source: '雪球', topic: '维权 / 兑付延期', count: 7, severity: 'high' },
    { source: '看准网', topic: '欠薪 / 大规模离职', count: 3, severity: 'high' },
    { source: '财经媒体（财新、第一财经）', topic: '调查报道', count: 4, severity: 'medium' },
    { source: '微博 / 知乎', topic: '产品安全质疑', count: 18, severity: 'medium' },
    { source: '政府投诉平台', topic: '消费者投诉', count: 6, severity: 'medium' },
  ],
  exec_personal: [
    '王某某 2010-2015 任职某 P2P 平台 CTO（该平台已暴雷被立案）',
    '王某某 名下另有 3 家公司（其一被列入失信名单）',
  ],
};

const STRATEGY = [
  {
    priority: 1,
    title: '财产保全 — 优先盯防支付宝 / 招商银行 / 上海银行',
    detail: '中标款流水主要走上海银行松江支行；个人转账多用支付宝',
    cite: '资产线索 #1，#3',
  },
  {
    priority: 2,
    title: '调查令调取 上海银行松江支行 流水',
    detail: '聚焦 2024-Q3 至今的大额转出（中标项目应收账款回款日附近）',
    cite: '资产线索 #1',
  },
  {
    priority: 3,
    title: '代位执行 — 5 项软著 + 1.8 亿在外应收账款',
    detail: '软著去版权保护中心办理转让公告即可锁定；应收账款查阅审计报告',
    cite: '资产线索 #2',
  },
  {
    priority: 4,
    title: '法庭情绪武器：央行处罚函 + 地方金融局 200 万罚单',
    detail: '该公司"违规开展类信贷"的认定，可作为本案合同性质（借贷 vs. 服务）的辅助论据',
    cite: '涉诉与监管 #1',
  },
  {
    priority: 5,
    title: '揭露代持 — 深圳前海智信资本管理 实为壳公司',
    detail: '注册资本仅 100 万却持有 15% 股权，强烈代持信号；穿透至王某某可能合并资产',
    cite: '股权穿透 #4',
  },
];

const RISK_BG: Record<string, string> = {
  high: 'bg-red-50 border-red-200 text-red-700',
  medium: 'bg-amber-50 border-amber-200 text-amber-700',
  low: 'bg-green-50 border-green-200 text-green-700',
};

const RISK_BAR: Record<string, string> = {
  high: 'bg-red-500',
  medium: 'bg-amber-500',
  low: 'bg-green-500',
};

export default function IntelligenceDashboardPage() {
  return (
    <div className="space-y-5 max-w-6xl">
      {/* Heading */}
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-wider text-gray-500">全景智能尽调引擎 · 演示</div>
          <h1 className="mt-1 text-2xl font-bold text-gray-900">{MOCK.company}</h1>
          <div className="mt-1 text-xs text-gray-500">
            统一社会信用代码 {MOCK.uscc} · 注册地 {MOCK.registered_address.split('号')[0]}号 ·
            {' '}成立 {MOCK.established}
          </div>
        </div>
        <div className="flex gap-2">
          <button className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50">
            导出 PDF 报告
          </button>
          <button className="rounded-md bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700">
            重新尽调
          </button>
        </div>
      </div>

      {/* Risk overview */}
      <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-center gap-4">
          {(['judicial', 'regulatory', 'sentiment'] as const).map((k) => {
            const r = MOCK.risk[k];
            return (
              <div key={k} className={`flex-1 min-w-[180px] rounded-lg border p-3 ${RISK_BG[r.level]}`}>
                <div className="text-xs font-semibold uppercase tracking-wider opacity-80">
                  {r.label}
                </div>
                <div className="mt-1 text-2xl font-bold">{r.score.toFixed(1)}<span className="text-sm font-normal opacity-60"> / 10</span></div>
                <div className="mt-2 h-1.5 w-full rounded-full bg-white/60">
                  <div className={`h-1.5 rounded-full ${RISK_BAR[r.level]}`} style={{ width: `${r.score * 10}%` }} />
                </div>
              </div>
            );
          })}
          <div className="flex-1 min-w-[180px] rounded-lg border border-gray-300 bg-gray-50 p-3">
            <div className="text-xs font-semibold uppercase tracking-wider text-gray-500">综合风险</div>
            <div className="mt-1 text-2xl font-bold text-gray-900">{MOCK.risk.composite.toFixed(1)}<span className="text-sm font-normal text-gray-500"> / 10</span></div>
            <div className="mt-2 text-xs text-gray-600">高风险被告 — 建议立即启动财产保全</div>
          </div>
        </div>
      </div>

      {/* 4-panel grid */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        {/* 股权穿透 */}
        <Panel title="股权穿透" subtitle="实控人 · 关联企业 · UBO" link="查看图谱 →">
          <div className="space-y-3">
            <div className="flex items-baseline justify-between">
              <span className="text-sm text-gray-600">实际控制人</span>
              <span className="text-sm font-medium text-gray-900">{EQUITY.ubo.name} ({EQUITY.ubo.stake}%)</span>
            </div>
            <div className="flex items-baseline justify-between">
              <span className="text-sm text-gray-600">关联公司数量</span>
              <span className="text-sm font-medium text-gray-900">{EQUITY.related_companies} 家（境外 BVI×{EQUITY.offshore.bvi} 开曼×{EQUITY.offshore.cayman}）</span>
            </div>
            <div>
              <div className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-gray-500">主要股东</div>
              <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200">
                {EQUITY.shareholders.map((s, i) => (
                  <li key={i} className="flex items-baseline justify-between p-2.5 text-sm">
                    <div>
                      <span className="font-medium text-gray-900">{s.name}</span>
                      <span className="ml-2 text-xs text-gray-500">{s.type}</span>
                    </div>
                    <span className="font-mono text-gray-700">{s.stake}%</span>
                  </li>
                ))}
              </ul>
            </div>
            <Flags items={EQUITY.flags} />
          </div>
        </Panel>

        {/* 涉诉与监管 */}
        <Panel title="涉诉与监管" subtitle="法律风险快照" link="看判决书 →">
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <Stat label="作为被告" value={LITIGATION.as_defendant} />
              <Stat label="作为原告" value={LITIGATION.as_plaintiff} />
              <Stat label="败诉率" value={`${(LITIGATION.loss_rate * 100).toFixed(0)}%`} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Stat label="未履行判决" value={LITIGATION.unfulfilled_judgments} bad />
              <Stat label="限消令" value="王某某 · 2025-Q3" bad />
            </div>
            <div>
              <div className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-gray-500">近期监管处罚</div>
              <ul className="space-y-2">
                {LITIGATION.regulatory.map((r, i) => (
                  <li key={i} className="rounded-lg border border-gray-200 bg-gray-50 p-2.5 text-sm">
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="font-medium text-gray-900">{r.issuer}</span>
                      <span className="text-xs text-gray-500">{r.date}</span>
                    </div>
                    <div className="mt-0.5 text-xs text-gray-700">{r.summary} · 罚款 {r.amount}</div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Panel>

        {/* 资产线索 */}
        <Panel title="资产线索" subtitle="可执行 / 可代位 财产" link="资产报告 →">
          <div className="space-y-3">
            <div>
              <div className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-gray-500">中标项目（应收账款线索）</div>
              <ul className="space-y-1.5">
                {ASSETS.bidding.map((b, i) => (
                  <li key={i} className="flex items-baseline justify-between rounded-md border border-gray-200 bg-white p-2 text-sm">
                    <div>
                      <div className="font-medium text-gray-900">{b.project}</div>
                      <div className="text-xs text-gray-500">{b.date} · {b.status}</div>
                    </div>
                    <div className="font-mono text-gray-700">{b.amount}</div>
                  </li>
                ))}
              </ul>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Stat label="软著" value={ASSETS.ip.copyright} />
              <Stat label="商标" value={ASSETS.ip.trademark} />
              <Stat label="专利" value={ASSETS.ip.patent} />
            </div>
            <Flags label="AI 推断的可执行线索" items={ASSETS.hints} good />
          </div>
        </Panel>

        {/* 舆情情报 */}
        <Panel title="舆情情报" subtitle="近 30 天负面 / 高管个人" link="舆情时间线 →">
          <div className="space-y-3">
            <div className="rounded-lg border border-red-200 bg-red-50 p-3">
              <div className="flex items-baseline justify-between">
                <span className="text-sm font-medium text-red-700">近 30 天负面情绪</span>
                <span className="text-2xl font-bold text-red-700">{SENTIMENT.negative_count_30d}<span className="text-sm font-normal opacity-70"> 篇</span></span>
              </div>
              <div className="mt-1 text-xs text-red-600">趋势：{SENTIMENT.trend === 'rising' ? '↑ 上升' : '↓ 下降'}</div>
            </div>
            <ul className="space-y-1.5">
              {SENTIMENT.buckets.map((b, i) => (
                <li key={i} className="flex items-center justify-between rounded-md border border-gray-200 p-2 text-sm">
                  <div>
                    <span className="font-medium text-gray-900">{b.source}</span>
                    <span className="ml-2 text-xs text-gray-600">{b.topic}</span>
                  </div>
                  <span className={`rounded-full px-2 py-0.5 text-xs ${b.severity === 'high' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                    {b.count}
                  </span>
                </li>
              ))}
            </ul>
            <Flags label="高管个人" items={SENTIMENT.exec_personal} />
          </div>
        </Panel>
      </div>

      {/* Recommended strategy */}
      <div className="rounded-xl border border-blue-200 bg-blue-50 p-5">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-blue-700">AI 推荐取证 / 保全策略</div>
            <h2 className="mt-1 text-lg font-bold text-gray-900">综合上述四项情报，建议执行：</h2>
          </div>
          <span className="rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-700">仅供参考</span>
        </div>
        <ol className="mt-4 space-y-3">
          {STRATEGY.map((s) => (
            <li key={s.priority} className="rounded-lg border border-blue-200 bg-white p-3">
              <div className="flex items-start gap-3">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-600 text-xs font-bold text-white">
                  {s.priority}
                </span>
                <div className="flex-1">
                  <div className="text-sm font-semibold text-gray-900">{s.title}</div>
                  <div className="mt-1 text-xs text-gray-600">{s.detail}</div>
                  <div className="mt-1 text-[10px] uppercase tracking-wider text-gray-400">参考 · {s.cite}</div>
                </div>
              </div>
            </li>
          ))}
        </ol>
      </div>

      {/* Disclaimer */}
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs text-gray-600">
        本页所有情报数据均为演示用例。实际产品将通过 企查查 / 天眼查 企业版 API、国家企业信用信息公示
        系统、中国裁判文书网、招标公开网、中国版权保护中心等合法数据源采集；舆情数据通过授权爬虫 +
        LLM 摘要生成。AI 推荐策略仅供办案律师参考，不构成法律意见。
      </div>
    </div>
  );
}

function Panel({
  title, subtitle, link, children,
}: { title: string; subtitle?: string; link?: string; children: React.ReactNode }) {
  return (
    <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
      <header className="mb-3 flex items-end justify-between">
        <div>
          <h2 className="text-base font-bold text-gray-900">{title}</h2>
          {subtitle && <div className="text-xs text-gray-500">{subtitle}</div>}
        </div>
        {link && (
          <button className="text-xs font-medium text-blue-600 hover:text-blue-700">
            {link}
          </button>
        )}
      </header>
      {children}
    </section>
  );
}

function Stat({ label, value, bad }: { label: string; value: number | string; bad?: boolean }) {
  return (
    <div className={`rounded-md border p-2.5 ${bad ? 'border-red-200 bg-red-50' : 'border-gray-200 bg-gray-50'}`}>
      <div className="text-[10px] uppercase tracking-wider text-gray-500">{label}</div>
      <div className={`mt-0.5 text-base font-bold ${bad ? 'text-red-700' : 'text-gray-900'}`}>{value}</div>
    </div>
  );
}

function Flags({ label = 'AI 标记的异常信号', items, good }: { label?: string; items: string[]; good?: boolean }) {
  return (
    <div>
      <div className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-gray-500">{label}</div>
      <ul className="space-y-1">
        {items.map((f, i) => (
          <li key={i} className="flex items-start gap-2 text-xs">
            <span className={`mt-1 h-1.5 w-1.5 shrink-0 rounded-full ${good ? 'bg-emerald-500' : 'bg-red-500'}`} />
            <span className="text-gray-700">{f}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
